import { message } from "antd";
import { type FunctionComponent, useCallback, useMemo, useState } from "react";
import { isPossiblePhoneNumber } from "react-phone-number-input";

import { BTPopover } from "@btnet/core-components/BTPopover";

import { BdsButton, BdsFormItem, BdsLayout, BdsSection } from "@buildertrend/components";

import { type Countries } from "types/countries";

import { showAPIErrorMessage } from "utilities/apiHandler";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTFormItem, BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTIconDeviceMobile } from "commonComponents/btWrappers/BTIcon";
import { BTLink } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import {
    CellEmailLookupHandler,
    type ICellEmailLookupHandler,
} from "commonComponents/entity/cellEmailLookup/CellEmailLookup/CellEmailLookup.api.handler";
import { CellPhoneField } from "commonComponents/entity/cellPhone/CellPhoneField/CellPhoneField";
import {
    type ICellPhoneInputFormValues,
    SmsOptInStatus,
    VerificationMessageType,
} from "commonComponents/entity/cellPhone/CellPhoneInput/CellPhoneInput.types";
import { CellPhoneOptInStatusTag } from "commonComponents/entity/cellPhone/CellPhoneInput/CellPhoneOptinStatusTag";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

import "react-phone-number-input/style.css";

export interface ICellPhoneInputProps {
    initialValues: ICellPhoneInputFormValues;
    values: ICellPhoneInputFormValues;
    defaultCountryCode: Countries;
    onChange: (
        field: keyof ICellPhoneInputFormValues,
        value: string | SmsOptInStatus | VerificationMessageType,
    ) => void;
    onBlur: (field: keyof ICellPhoneInputFormValues) => void;
    onPreferencesClicked?: () => void;
    handler?: ICellEmailLookupHandler;
    readOnly?: boolean;
    label?: string;
    useBds?: boolean;
}

interface ITextVerificationSectionProps {
    cellPhoneNumber: string;
    smsOptInStatus: SmsOptInStatus;
    disabled: boolean;
    verificationRequest: VerificationMessageType;
    onOptIn: () => Promise<void>;
    onOptOut: () => void;
    onCancel: () => void;
    isLoading: boolean;
    useBds?: boolean;
}

export const TextVerificationSection: FunctionComponent<ITextVerificationSectionProps> = ({
    smsOptInStatus,
    disabled,
    verificationRequest,
    onOptIn,
    onOptOut,
    onCancel,
    isLoading,
    useBds,
}) => {
    if (verificationRequest !== VerificationMessageType.None) {
        const isOptIn = verificationRequest === VerificationMessageType.OptIn;
        const cancelButtonText = isOptIn ? "Cancel verification" : "Cancel opt out";

        const verficationStatusType = isOptIn ? "" : "success";

        if (useBds) {
            const verificationStatusText = isOptIn ? (
                <>
                    Verification text
                    <br />
                    sent on save
                </>
            ) : (
                "Opted in"
            );
            return (
                <>
                    <BdsButton
                        id="CellPhoneInput-Cancel"
                        testid="CellPhoneInput-Cancel"
                        className="margin-right-xs"
                        onClick={onCancel}
                        icon={<BTIconDeviceMobile />}
                        text={cancelButtonText}
                        loading={isLoading}
                        displayType="secondary"
                    />
                    <StatusTagDisplay
                        statusType={verficationStatusType}
                        statusText={verificationStatusText}
                        useBds
                    />
                </>
            );
        }
        const verificationStatusText = isOptIn ? "Verification text sent on save" : "Opted in";

        if (useBds) {
            return (
                <>
                    <BdsButton
                        id="CellPhoneInput-Cancel"
                        testid="CellPhoneInput-Cancel"
                        className="margin-right-xs"
                        onClick={onCancel}
                        icon={<BTIconDeviceMobile />}
                        text={cancelButtonText}
                        loading={isLoading}
                    />
                    <StatusTagDisplay
                        statusType={verficationStatusType}
                        statusText={verificationStatusText}
                        useBds
                    />
                </>
            );
        }

        return (
            <>
                <BTButtonDeprecated
                    id="CellPhoneInput-Cancel"
                    data-testid="CellPhoneInput-Cancel"
                    className="margin-right-xs"
                    onClick={onCancel}
                    icon={<BTIconDeviceMobile />}
                    loading={isLoading}
                >
                    {cancelButtonText}
                </BTButtonDeprecated>
                <StatusTagDisplay
                    statusType={verficationStatusType}
                    statusText={verificationStatusText}
                />
            </>
        );
    }

    if (useBds) {
        return (
            <>
                {smsOptInStatus === SmsOptInStatus.None && (
                    <BdsButton
                        id="CellPhoneInput-OptIn"
                        testid="CellPhoneInput-OptIn"
                        className="margin-right-xs"
                        onClick={onOptIn}
                        disabled={disabled}
                        loading={isLoading}
                        icon={<BTIconDeviceMobile />}
                        text="Verify phone number"
                        displayType="secondary"
                    />
                )}
                {smsOptInStatus === SmsOptInStatus.OptedIn && (
                    <BdsButton
                        id="CellPhoneInput-OptOut"
                        testid="CellPhoneInput-OptOut"
                        className="margin-right-xs"
                        onClick={onOptOut}
                        disabled={disabled}
                        loading={isLoading}
                        icon={<BTIconDeviceMobile />}
                        text="Opt out"
                        displayType="secondary"
                    />
                )}
                {smsOptInStatus !== SmsOptInStatus.None && (
                    <CellPhoneOptInStatusTag smsOptInStatus={smsOptInStatus} useBds />
                )}
            </>
        );
    }

    // TODO: Users will not receive any text notifications if they have opted out, including opt in texts.
    // We need to provide some information indicating they need to reply "START" to one of the BT twilio numbers to be able to get the opt in text

    return (
        <>
            {smsOptInStatus === SmsOptInStatus.None && (
                <BTButtonDeprecated
                    id="CellPhoneInput-OptIn"
                    data-testid="CellPhoneInput-OptIn"
                    className="margin-right-xs"
                    name="OptIn"
                    onClick={onOptIn}
                    disabled={disabled}
                    loading={isLoading}
                    icon={<BTIconDeviceMobile />}
                >
                    Verify phone number
                </BTButtonDeprecated>
            )}
            {smsOptInStatus === SmsOptInStatus.OptedIn && (
                <BTButtonDeprecated
                    id="CellPhoneInput-OptOut"
                    data-testid="CellPhoneInput-OptOut"
                    className="margin-right-xs"
                    name="OptOut"
                    onClick={onOptOut}
                    disabled={disabled}
                    loading={isLoading}
                    icon={<BTIconDeviceMobile />}
                >
                    Opt out
                </BTButtonDeprecated>
            )}
            {smsOptInStatus !== SmsOptInStatus.None && (
                <CellPhoneOptInStatusTag smsOptInStatus={smsOptInStatus} />
            )}
        </>
    );
};

const defaultHandler = new CellEmailLookupHandler();
export const CellPhoneInput: FunctionComponent<ICellPhoneInputProps> = ({
    values,
    defaultCountryCode,
    onChange,
    onBlur,
    initialValues,
    onPreferencesClicked,
    handler = defaultHandler,
    readOnly = false,
    label = "Cell Phone",
    useBds,
}) => {
    const [isPopoverVisible, setIsPopoverVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const updateVerification = (value: VerificationMessageType) => {
        onChange("verificationRequest", value);
    };

    const handleOptIn = async () => {
        try {
            setIsLoading(true);
            const apiResponse = await handler.lookup(values.phoneCell);
            if (apiResponse.domainMessage) {
                void message.error(apiResponse.domainMessage);
            } else {
                updateVerification(VerificationMessageType.OptIn);
            }
        } catch (e) {
            showAPIErrorMessage(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOptOut = () => {
        updateVerification(VerificationMessageType.OptOut);
    };

    const handleCancel = () => {
        updateVerification(VerificationMessageType.None);
    };

    const handleCellPhoneChanged = useCallback(
        (value?: string) => {
            onChange("phoneCell", value ?? "");
            if (values.verificationRequest !== VerificationMessageType.None) {
                onChange("verificationRequest", VerificationMessageType.None);
            }
        },
        [values.verificationRequest, onChange],
    );

    const handleCellPhoneTouched = useCallback(() => {
        onBlur("phoneCell");
    }, [onBlur]);

    const handlePopoverVisibleChange = (visible: boolean) => {
        setIsPopoverVisible(visible);
    };

    const handlePreferencesLinkClicked = useCallback(() => {
        setIsPopoverVisible(false);
        if (onPreferencesClicked) {
            onPreferencesClicked();
        }
    }, [setIsPopoverVisible, onPreferencesClicked]);

    const popoverContext = useMemo(
        () => (
            <>
                To receive text messages
                <br />
                please review your
                <br />
                {onPreferencesClicked && (
                    <BTLink to="#" linkType="link" onClick={handlePreferencesLinkClicked}>
                        notification preferences.
                    </BTLink>
                )}
                {!onPreferencesClicked && <>notification preferences.</>}
            </>
        ),
        [handlePreferencesLinkClicked, onPreferencesClicked],
    );

    let smsOptInStatus = initialValues.smsOptInStatus;
    if (values.phoneCell !== initialValues.phoneCell) {
        smsOptInStatus = SmsOptInStatus.None;
    }

    if (useBds) {
        return (
            <BdsLayout
                displayType="1and1"
                gap={[
                    { dir: "hz", break: "min", size: "2" },
                    { dir: "vt", break: "min", size: "4" },
                ]}
            >
                <BdsSection>
                    <BTFormItemAutomatic<ICellPhoneInputFormValues>
                        id="phoneCell"
                        label={label}
                        useBds
                    >
                        <CellPhoneField
                            id="phoneCell"
                            value={values.phoneCell}
                            onChange={(_, value) => handleCellPhoneChanged(value)}
                            defaultCountryCode={defaultCountryCode}
                            onBlur={handleCellPhoneTouched}
                            readOnly={readOnly}
                            isBds
                        />
                    </BTFormItemAutomatic>
                </BdsSection>
                <BdsSection>
                    <BdsFormItem
                        label={
                            <>
                                Receive text messages
                                <BTPopover
                                    visible={isPopoverVisible}
                                    placement="right"
                                    onVisibleChange={handlePopoverVisibleChange}
                                    breakWord={false}
                                    content={popoverContext}
                                />
                            </>
                        }
                    >
                        <TextVerificationSection
                            cellPhoneNumber={values.phoneCell}
                            smsOptInStatus={smsOptInStatus}
                            verificationRequest={values.verificationRequest}
                            disabled={!isPossiblePhoneNumber(values.phoneCell) || readOnly}
                            onOptIn={handleOptIn}
                            onOptOut={handleOptOut}
                            onCancel={handleCancel}
                            isLoading={isLoading}
                            useBds
                        />
                    </BdsFormItem>
                </BdsSection>
            </BdsLayout>
        );
    }

    return (
        <BTRow gutter={8}>
            <BTCol xs={24} md={12} lg={10}>
                <BTFormItemAutomatic<ICellPhoneInputFormValues> id="phoneCell" label={label}>
                    <CellPhoneField
                        id="phoneCell"
                        value={values.phoneCell}
                        onChange={(_, value) => handleCellPhoneChanged(value)}
                        defaultCountryCode={defaultCountryCode}
                        onBlur={handleCellPhoneTouched}
                        readOnly={readOnly}
                    />
                </BTFormItemAutomatic>
            </BTCol>
            <BTCol xs={24} md={12} lg={10}>
                <BTFormItem
                    label={
                        <>
                            Receive text messages
                            <BTPopover
                                visible={isPopoverVisible}
                                placement="right"
                                onVisibleChange={handlePopoverVisibleChange}
                                breakWord={false}
                                content={popoverContext}
                            />
                        </>
                    }
                >
                    <TextVerificationSection
                        cellPhoneNumber={values.phoneCell}
                        smsOptInStatus={smsOptInStatus}
                        verificationRequest={values.verificationRequest}
                        disabled={!isPossiblePhoneNumber(values.phoneCell) || readOnly}
                        onOptIn={handleOptIn}
                        onOptOut={handleOptOut}
                        onCancel={handleCancel}
                        isLoading={isLoading}
                    />
                </BTFormItem>
            </BTCol>
        </BTRow>
    );
};
